import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../services/api';
import CaseFormWizard from '../components/CaseFormWizard';
import { CaseFormData, initialFormData, SENSITIVITY_FLAGS } from '../types/caseForm';

const CreateCase: React.FC = () => {
  const [formData, setFormData] = useState<CaseFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  // Auto-fill documenter details from logged-in user
  React.useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        documentorName: `${user.firstName} ${user.lastName}`,
        documentorEmail: user.email,
        documentorOrganization: user.organization || ''
      }));
    }
  }, [user]);

  const handleSubmit = async (asDraft: boolean) => {
    setLoading(true);
    setError('');

    try {
      // Build the start date based on precision
      let processedStartDate = formData.caseStartDate;
      if (formData.caseStartDatePrecision === 'YEAR' && formData.caseStartDate) {
        processedStartDate = `${formData.caseStartDate}-01-01`;
      } else if (formData.caseStartDatePrecision === 'MONTH' && formData.caseStartDate) {
        processedStartDate = `${formData.caseStartDate}-01`;
      }

      const incidentDateISO = processedStartDate ? new Date(processedStartDate).toISOString() : null;

      const caseData: Record<string, any> = {
        // Required
        incidentDate: incidentDateISO,
        description: formData.description,
        violationTypes: formData.violationTypes,
        country: formData.country,
        indigenousGroup: formData.indigenousGroup.join(', '),

        // Documenter
        documentorName: formData.documentorName,
        documentorOrganization: formData.documentorOrganization,
        documentorEmail: formData.documentorEmail,
        documentorPhone: formData.documentorPhone,
        isPrimaryData: formData.isPrimaryData,
        dataSource: 'Web',
        dataUsageRestriction: formData.dataUsageRestriction,
        willingToShareMoreInfo: formData.willingToShareMoreInfo,
        isLDSFApplicant: formData.isLDSFApplicant,
        ldsfPaymentCompletedDate: formData.isLDSFApplicant && formData.ldsfPaymentCompletedDate ? formData.ldsfPaymentCompletedDate : null,
        sourceInformation: formData.sourceInformation,

        // Incident details
        incidentTime: formData.incidentTime,
        otherViolationTypes: formData.otherViolationTypes,
        otherViolationTypeDetails: formData.otherViolationTypeDetails,
        isCaseOngoing: formData.isCaseOngoing,
        caseStartDate: processedStartDate,
        caseStartDatePrecision: formData.caseStartDatePrecision,
        caseEndDate: formData.caseEndDate || null,
        reportedDate: formData.reportedDate || new Date().toISOString().split('T')[0],

        // Location
        region: formData.region,
        city: formData.city,
        specificLocation: formData.specificLocation,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        indigenousTerritory: formData.indigenousTerritory,

        // Violation details
        violationDetails: formData.violationDetails,

        // Impact
        familyImpact: formData.familyImpact,
        communityImpact: formData.communityImpact,
        investigationConducted: formData.investigationConducted,
        investigationDetails: formData.investigationDetails,
        investigationWhyNot: formData.investigationWhyNot,
        advocacyUndertaken: formData.advocacyUndertaken,
        advocacyDetails: formData.advocacyDetails,
        advocacyWhyNot: formData.advocacyWhyNot,

        // Development project
        isRelatedToDevelopmentProject: formData.isRelatedToDevelopmentProject,
        developmentProjectType: formData.developmentProjectType,
        developmentProjectName: formData.developmentProjectName,
        developmentProjectDescription: formData.developmentProjectDescription,
        developmentProjectStartYear: formData.developmentProjectStartYear ? parseInt(formData.developmentProjectStartYear) : null,
        developmentProjectSource: formData.developmentProjectSource,
        developmentProjectWebsiteLink: formData.developmentProjectWebsiteLink,

        // Victims & perpetrators
        victims: formData.victims,
        perpetrators: formData.perpetrators,

        // Metadata
        priority: formData.priority,
        tags: formData.tags,
        isPublic: formData.isPublic,

        submittedById: user?.id,
        status: asDraft ? 'DRAFT' : 'FOR_VALIDATION',
      };

      const response = await API.post('/api/cases', caseData);
      const caseId = response.data.case.id;

      if (formData.files.length > 0) {
        for (const caseFile of formData.files) {
          const formDataForFile = new FormData();
          formDataForFile.append('documents', caseFile.file);
          formDataForFile.append('category', caseFile.category);
          const sensitivityPrefixes = caseFile.sensitivityWarnings
            .map(w => SENSITIVITY_FLAGS.find(f => f.value === w)?.prefix)
            .filter(Boolean)
            .join(' ');
          const fullDescription = sensitivityPrefixes
            ? `${sensitivityPrefixes} ${caseFile.description || caseFile.file.name}`
            : (caseFile.description || caseFile.file.name);
          formDataForFile.append('description', fullDescription);
          if (caseFile.sensitivityWarnings.length > 0) formDataForFile.append('sensitivityWarnings', JSON.stringify(caseFile.sensitivityWarnings));
          if (caseFile.documentDate) formDataForFile.append('documentDate', caseFile.documentDate);
          if (caseFile.authorSource) formDataForFile.append('authorSource', caseFile.authorSource);
          if (caseFile.language) formDataForFile.append('language', caseFile.language);
          formDataForFile.append('confidentiality', caseFile.confidentiality);

          try {
            await API.post(`/api/documents/case/${caseId}`, formDataForFile, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          } catch (fileError: any) {
            console.warn(`File upload failed for ${caseFile.file.name}:`, fileError);
          }
        }
      }

      navigate(`/cases/${caseId}`);
    } catch (err: any) {
      const data = err.response?.data;
      let msg = data?.message || 'Failed to create case';
      if (data?.missingFields?.length) {
        msg += ` Missing: ${data.missingFields.join(', ')}`;
      }
      setError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 font-heading">Create New Case</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Fill out all required fields to submit a new case
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      <CaseFormWizard
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </div>
  );
};

export default CreateCase;
